<template>
  <v-container class="feature-config" fluid>
    <v-row class="mx-0" style="height:48px;">
      <h2 class="feature-config__title">Feature Configs</h2>
    </v-row>

    <v-row class="mx-0 pb-4">
      <select-project
        v-model="project"
        style="min-width: 250px; max-width: 250px"
      />
    </v-row>

    <template>
    <v-card class="d-flex align-center feature-config__toolbar pr-4">
      <template v-if="selectedMirror">
        <v-btn
          icon
          small
          color="success"
          class="ml-4 mr-1"
          @click="onCancel"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <span class="body-1">{{ mirrorEditTitle }}</span>
        <v-spacer />
        <div v-if="canEditFeatureConfigs" class="d-flex align-center" style="gap: 16px">
          <v-btn
            outlined
            small
            color="error"
            class="px-2"
            @click="onCancel"
          >
            Cancel
          </v-btn>
          <v-btn
            outlined
            small
            color="primary"
            :loading="saving"
            :disabled="!editConfigHasChanges"
            class="px-2"
            @click="onSave"
          >
            Save Changes
          </v-btn>
        </div>
      </template>

      <template v-else>
        <v-tabs
          :show-arrows="$vuetify.breakpoint.name == 'xs' || $vuetify.breakpoint.name == 'sm'"
          :left="$vuetify.breakpoint.name !== 'xs' && $vuetify.breakpoint.name !== 'sm'"
          v-model="activeTab"
          class="flex-grow-1"
        >
          <v-tab href="#project-config">Project Config</v-tab>
          <v-tab href="#mirrors-configs">Mirrors Configs</v-tab>

          <div v-if="canEditFeatureConfigs && activeTab === 'project-config'" class="d-flex ml-auto align-self-center" style="gap: 16px">
            <v-btn
              outlined
              small
              color="error"
              class="px-2"
              @click="onCancel"
            >
              Cancel
            </v-btn>
            <v-btn
              outlined
              small
              color="primary"
              :loading="saving"
              :disabled="!editConfigHasChanges"
              class="px-2"
              @click="onSave"
            >
              Save Changes
            </v-btn>
          </div>
        </v-tabs>
      </template>
    </v-card>

    <project-config-tab
      v-if="!loading"
      ref="projectConfigTab"
      v-show="activeTab === 'project-config'"
      :project="project"
      :loading="loading"
      :project-data="projectData"
      :main-domain-data="mainDomainData"
      :selected-mirror="selectedMirror"
      :licenses="licenses"
      :supports="supports"
      :readonly="!canEditFeatureConfigs"
      @update:hasChanges="editConfigHasChanges = $event"
      @maintenance-submit="onMaintenanceSubmit"
    />
    <mirrors-configs-tab
      v-show="activeTab === 'mirrors-configs'"
      :project="project"
      :loading="loading"
      :mirrors-list="mirrorsList"
      @select-mirror="onSelectMirror"
    />
    </template>
  </v-container>
</template>

<script>
  import { cloneDeep, isEqual } from 'lodash'
  import { API } from '@/utils/api'
  import SelectProject from '@/components/core/SelectProject/SelectProject.vue'

  export default {
    name: 'FeatureConfig',
    components: {
      SelectProject,
      ProjectConfigTab: () => import('./ProjectConfigTab.vue'),
      MirrorsConfigsTab: () => import('./MirrorsConfigsTab.vue'),
    },
    data: () => ({
      project: null,
      activeTab: 'project-config',
      loading: false,
      saving: false,
      editConfigHasChanges: false,
      projectData: null,
      mainDomainData: null,
      mirrorsList: [],
      selectedMirror: null,
      licenses: [],
      supports: [],
    }),
    computed: {
      mirrorEditTitle() {
        if (!this.selectedMirror) return ''
        const idx = this.mirrorsList.findIndex(
          (m) => m.settings_id === this.selectedMirror.settings_id
        )
        const n = idx >= 0 ? idx + 1 : ''
        const name = this.selectedMirror.mirror_name || ''
        return n ? `#${n} Mirror: ${name}` : `Mirror: ${name}`
      },
      canEditFeatureConfigs() {
        return this.$store.state.auth.checkAccess({ key: 'feature_configs_edit', value: true })
      },
    },

    watch: {
      project(entityId) {
        this.editConfigHasChanges = false
        this.selectedMirror = null
        if (entityId) {
          this.loadSettings(entityId)
        } else {
          this.projectData = null
          this.mainDomainData = null
          this.mirrorsList = []
          this.licenses = []
          this.supports = []
        }
      },

      '$route.query.entity_id': {
        handler() {
          this.applyProjectFromRoute()
        },
        immediate: true,
      },
    },

    methods: {
      buildMirrorDiff(mirrorSettings = {}, projectSettings = {}) {
        const diff = {}

        Object.keys(mirrorSettings).forEach((key) => {
          if (!isEqual(mirrorSettings[key], projectSettings[key])) {
            diff[key] = cloneDeep(mirrorSettings[key])
          } else{
            diff[key] = {}
          }
        })

        return diff
      },

      normalizeFeatureConfigForSave(featureConfig) {
        if (!featureConfig?.mirror_id) {
          return featureConfig
        }

        const projectSettings = this.projectData?.settings || {}
        const mirrorSettings = featureConfig?.settings || {}
        const diffSettings = this.buildMirrorDiff(mirrorSettings, projectSettings)

        return {
          ...featureConfig,
          settings: diffSettings,
        }
      },
      applyProjectFromRoute() {
          const id = Number(this.$route.query.entity_id)
          if (!isNaN(id) && id && this.project !== id) {
            this.project = id
        }
      },
      onSelectMirror(mirror) {
        this.selectedMirror = mirror
        this.activeTab = 'project-config'
        this.editConfigHasChanges = false
      },
      onCancel() {
        this.$refs.projectConfigTab?.$refs?.configEditor?.onCancel()
        if (this.selectedMirror) {
          this.selectedMirror = null
          this.activeTab = 'mirrors-configs'
        }
      },
      async saveSettings(featureConfig) {
        if (!this.canEditFeatureConfigs) return

        this.saving = true
        try {
          const normalizedConfig = this.normalizeFeatureConfigForSave(featureConfig)

          await API.call('/$v/mirrors-redirectors/settings/update.json', {
            entity_id: this.project,
            feature_config: normalizedConfig,
          })
          this.$showSuccess?.('Saved successfully')
          await this.loadSettings(this.project)
        } catch (e) {
          this.$showError?.(e?.response?.data?.message || 'Failed to save')
        } finally {
          this.saving = false
        }
      },

      async onSave() {
        if (!this.canEditFeatureConfigs) return

        const editor = this.$refs.projectConfigTab?.$refs?.configEditor
        if (!editor) return
        await this.saveSettings(editor.buildPayload())
      },

      async onMaintenanceSubmit({ feature_config, maintenance }) {
        if (!this.canEditFeatureConfigs) return

        await this.saveSettings({
          ...feature_config,
          settings: {
            maintenance: Boolean(maintenance),
          },
        })
      },

      async loadSettings(entityId) {
        this.loading = true
        this.projectData = null
        this.mirrorsList = []
        try {
          const response = await API.call('/$v/mirrors-redirectors/settings/list.json', {
            entity_id: entityId,
          })
          const data = response.data ?? {}
          this.projectData = data.project_feature_config ?? null
          this.mainDomainData = data.main_domain ?? null
          this.mirrorsList = Array.isArray(data.mirrors_feature_config) ? data.mirrors_feature_config : []
          this.licenses = Array.isArray(data.licenses) ? data.licenses : []
          this.supports = Array.isArray(data.supports) ? data.supports : []

          if (this.selectedMirror) {
            this.selectedMirror = this.mirrorsList.find(
            (m) => m.settings_id === this.selectedMirror.settings_id
          ) || null
          } else {
            this.selectedMirror = null
          }

          const mirrorIdFromRoute = this.$route.query.mirror_id
          if (mirrorIdFromRoute != null && mirrorIdFromRoute !== '') {
            const mirror = this.mirrorsList.find(
              (m) => String(m.mirror_id) === String(mirrorIdFromRoute)
            )
            if (mirror) {
              this.selectedMirror = mirror
            }
            this.$router.replace({
              path: '/feature-config',
              query: { entity_id: String(entityId) },
            })
          }

        } catch (e) {
          this.$showError(e?.response?.data?.message || 'Failed to load settings')
        } finally {
          this.loading = false
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
  .feature-config {

    &__title {
      font-size: 20px;
      line-height: 40px;
      font-weight: 400;
    }

    &__toolbar {
      min-height: 48px;
    }

    &__empty-body {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 280px;
    }
  }
</style>
